// Routage (ADR 0013) : TanStack Router en routes déclarées (chemins littéraux typés) ; chaque page est chargée à la
// demande (découpage du code par route, budget JS initial ≤ 200 Ko gzip). URLs en français, lisibles et partageables.
// Parcours d'accès publics (A14, ADR 0023) hors de la garde d'authentification.
import { createRootRoute, createRoute, createRouter, lazyRouteComponent, Outlet, redirect, useNavigate, type RouterHistory } from '@tanstack/react-router';
import type { ComponentType } from 'react';
import { AppShell } from '@/shells/AppShell';
import { FocusShell } from '@/shells/FocusShell';
import { NotFoundState } from '@/design-system/States';
import { parseListSearch } from '@/lib/list-query';
import { prefersDataSaving } from '@/lib/network';
import type { ActivitiesSearch, ActivityDetailSearch, ActivityFormSearch, CalendarSearch, GanttSearch, ReviewSearch } from '@/modules/activities/search';
import { parseIdentifierSearch, parseLoginSearch, parsePeriodSearch, parseResetSearch, parseUserDetailSearch, parseViewAsSearch } from '@/modules/access/search';
import type { BudgetsSearch, BudgetViewSearch, ComparisonSearch, ExpenseEntrySearch, ExpenseQueueSearch, ExpensesSearch, ProposalsSearch } from '@/modules/budget/search';
import type { ReportEditorSearch, ReportQueueSearch, ReportsSearch } from '@/modules/reports/search';
import type { ExplorerSearch, RawExportSearch } from '@/modules/analytics/search';
import type { ProjectComparisonSearch } from '@/modules/dashboards/pages/DSH-P03.Comparison';
import type { GenerateReportSearch } from '@/modules/reports/pages/RAP-W01.GenerateReport';
import type { CustomReportsSearch } from '@/modules/analytics/pages/ANA-P03.CustomReports';
import type { BuilderSearch, CollectionDetailSearch, CollectionsSearch, ParticipantsSearch, QueueSearch } from '@/modules/field/search';
import type { ProjectViewSearch, WizardSearch } from '@/modules/projects/search';
import { parseLocalitySearch, parseReferentialDetailSearch } from '@/modules/referentials/search';
import { AuthGuard } from './AuthGuard';

function NotFoundPage() {
  const navigate = useNavigate();
  return <NotFoundState onHome={() => void navigate({ to: '/' })} />;
}

/**
 * Recherche typée sans code au démarrage (budget JS initial) : la page, chargée à la demande, valide elle-même ses
 * paramètres (modules/activities/search.ts).
 */
const searchAs =
  <T,>() =>
  (raw: Record<string, unknown>): T =>
    raw as T;

/**
 * Page de l'application (sous la garde d'authentification), chargée à la demande. Fabrique commune : chaque route
 * déclarée en une ligne pèse moins dans le JavaScript initial (budget ≤ 200 Ko gzip, lot F5).
 */
function page<TPath extends string>(path: TPath, load: () => Promise<{ default: ComponentType }>) {
  return createRoute({ getParentRoute: () => appRoute, path, component: lazyRouteComponent(load) });
}

function searchPage<TPath extends string, TSearch>(path: TPath, load: () => Promise<{ default: ComponentType }>, validateSearch: (raw: Record<string, unknown>) => TSearch) {
  return createRoute({ getParentRoute: () => appRoute, path, validateSearch, component: lazyRouteComponent(load) });
}
const rootRoute = createRootRoute({ component: Outlet, notFoundComponent: NotFoundPage });

/** Page publique (hors garde d'authentification), chargée à la demande : même fabrique compacte que `page()`. */
function publicPage<TPath extends string>(path: TPath, load: () => Promise<{ default: ComponentType }>) {
  return createRoute({ getParentRoute: () => rootRoute, path, component: lazyRouteComponent(load) });
}

function publicSearchPage<TPath extends string, TSearch>(path: TPath, load: () => Promise<{ default: ComponentType }>, validateSearch: (raw: Record<string, unknown>) => TSearch) {
  return createRoute({ getParentRoute: () => rootRoute, path, validateSearch, component: lazyRouteComponent(load) });
}

// --- Parcours d'accès publics (ACC-P01 à P07, PUB-P03, PUB-P07) ---------------------------------------------------
const loginRoute = publicSearchPage('/connexion', () => import('@/modules/access/pages/ACC-P01.Login'), parseLoginSearch);
// Lot F13e (ACC-14) : second facteur.
const twoFactorRoute = publicSearchPage('/connexion/verification', () => import('@/modules/access/pages/ACC-P02.TwoFactorChallenge'), searchAs<{ redirect?: string }>());
const forgotRoute = publicSearchPage('/mot-de-passe/oublie', () => import('@/modules/access/pages/ACC-P03.ForgotPassword'), parseIdentifierSearch);
const resetRoute = publicSearchPage('/mot-de-passe/nouveau', () => import('@/modules/access/pages/ACC-P04.ResetPassword'), parseResetSearch);
const activationRoute = publicPage('/activation/$token', () => import('@/modules/access/pages/ACC-P05.ActivateAccount'));
const refusalRoute = publicSearchPage('/compte/$state', () => import('@/modules/access/pages/ACC-P06.AccountRefused'), parseIdentifierSearch);
const signedOutRoute = publicPage('/deconnexion', () => import('@/modules/access/pages/ACC-P07.SignedOut'));
const registerRoute = publicSearchPage('/inscription', () => import('@/modules/access/pages/PUB-P07.RegisterLearner'), searchAs<{ redirect?: string }>());
const verifyRoute = publicPage('/inscription/verification', () => import('@/modules/access/pages/PUB-P03.VerifyCode'));

// Portail public des offres d'emploi (PUB-P08, PUB-P09, PUB-W01 ; RH-24, RH-25, lot F10c) : sans compte.
const publicOffersRoute = publicSearchPage('/offres', () => import('@/modules/careers/pages/PUB-P08.JobOffers'), searchAs<{ lieu?: string; contrat?: string }>());
const publicOfferRoute = publicPage('/offres/$slug', () => import('@/modules/careers/pages/PUB-P09.JobOffer'));
const publicApplyRoute = publicPage('/offres/$slug/candidature', () => import('@/modules/careers/pages/PUB-W01.Apply'));

// Portail public et vie associative (PUB-P01, PUB-P02, PUB-P04, PUB-P10 ; ASSO-01, ASSO-05, lot F12a) : sans compte.
const portalRoute = publicPage('/portail', () => import('@/modules/association/pages/PUB-P01.Portal'));
const membershipRequestRoute = publicPage('/adhesion', () => import('@/modules/association/pages/PUB-P02.MembershipRequest'));
const membershipTrackingRoute = publicSearchPage('/adhesion/suivi/$token', () => import('@/modules/association/pages/PUB-P04.MembershipTracking'), searchAs<{ nouvelle?: boolean }>());
const verifyMemberRoute = publicSearchPage('/verifier', () => import('@/modules/association/pages/PUB-P10.VerifyMemberCard'), searchAs<{ numero?: string; recu?: string; pv?: string }>());
const verifyMemberTokenRoute = publicPage('/verifier/membre/$token', () => import('@/modules/association/pages/PUB-P10.VerifyMemberCard'));

// --- Application authentifiée ------------------------------------------------------------------------------------
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  component: () => (
    <AuthGuard>
      <AppShell />
    </AuthGuard>
  ),
});

// Pages plein écran sans navigation (lot F14c) : lecteur de contenu ACA-P09, questionnaire de satisfaction ACA-P12.
const focusRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'focus',
  component: () => (
    <AuthGuard>
      <FocusShell />
    </AuthGuard>
  ),
});

function focusPage<TPath extends string, TSearch>(path: TPath, load: () => Promise<{ default: ComponentType }>, validateSearch: (raw: Record<string, unknown>) => TSearch) {
  return createRoute({ getParentRoute: () => focusRoute, path, validateSearch, component: lazyRouteComponent(load) });
}

// Accueils par rôle (HOME-P01 à P10, lot F7a) : accueil du profil principal, onglets pour les autres casquettes.
const indexRoute = searchPage('/', () => import('@/modules/home/pages/Home'), searchAs<{ accueil?: string }>());
const mySpaceRoute = page('/moi', () => import('@/modules/home/pages/HOME-P11.MySpace'));

const profileRoute = page('/moi/profil', () => import('@/modules/me/pages/SYS-P02.MyProfile'));
const securityRoute = page('/moi/securite', () => import('@/modules/me/pages/SYS-P03.AccountSecurity'));
const activityRoute = searchPage('/moi/activite', () => import('@/modules/me/pages/AUD-P03.MyActivity'), parseListSearch);
const preferencesRoute = page('/moi/preferences', () => import('@/modules/me/pages/SYS-P04.Preferences'));
// TRV-10 (lot D3) : droit d'accès et de rectification — mes données (SYS-P06), traitement des demandes (TRV-P07).
const personalDataRoute = page('/moi/donnees-personnelles', () => import('@/modules/me/pages/SYS-P06.PersonalData'));
const rectificationsRoute = page('/admin/rectifications', () => import('@/modules/platform/pages/TRV-P07.RectificationRequests'));
const myExportsRoute = searchPage('/moi/exports', () => import('@/modules/platform/pages/TRV-P05.MyExports'), parseListSearch);

// Notifications, préférences, règles d'alerte, recherche globale (ALR-P01 à P03, SYS-P01 — lot F7a).
const notificationsRoute = searchPage('/notifications', () => import('@/modules/alerts/pages/ALR-P01.NotificationCenter'), searchAs<{ onglet?: 'toutes'; module?: string; niveaux?: string; page?: number; ouvrir?: string }>());
const notificationPreferencesRoute = page('/moi/notifications', () => import('@/modules/alerts/pages/ALR-P02.NotificationPreferences'));
const alertRulesRoute = page('/admin/alertes', () => import('@/modules/alerts/pages/ALR-P03.AlertRules'));
const searchRoute = searchPage('/recherche', () => import('@/modules/alerts/pages/SYS-P01.GlobalSearch'), searchAs<{ q?: string; categorie?: string }>());

// Tableaux de bord (DSH-P01, DSH-P04, DSH-P05 — lot F7a).
const portfolioRoute = page('/tableaux-de-bord/projets', () => import('@/modules/dashboards/pages/DSH-P01.Portfolio'));
// Carte des zones d'intervention (DSH-P02, DSH-03, lot F13c).
const interventionMapRoute = page('/tableaux-de-bord/carte', () => import('@/modules/dashboards/pages/DSH-P02.Map'));
const comparisonRoute = searchPage('/tableaux-de-bord/comparaison', () => import('@/modules/dashboards/pages/DSH-P03.Comparison'), searchAs<ProjectComparisonSearch>());
const myStatisticsRoute = page('/tableaux-de-bord/mes-statistiques', () => import('@/modules/dashboards/pages/DSH-P04.MyStatistics'));
const statisticsRoute = searchPage('/tableaux-de-bord/statistiques', () => import('@/modules/dashboards/pages/DSH-P05.Statistics'), searchAs<{ onglet?: string }>());

// Administration des comptes, rôles et audit (ACC, ROL, AUD — lot F1).
const usersRoute = searchPage('/admin/utilisateurs', () => import('@/modules/access/pages/ACC-P08.Users'), parseListSearch);
// `?dossier=` : compte créé depuis un dossier du personnel (RH-P02.V9, lot F10a).
const userCreateRoute = searchPage('/admin/utilisateurs/nouveau', () => import('@/modules/access/pages/ACC-W01.CreateUser'), searchAs<{ dossier?: string }>());
const userImportRoute = page('/admin/utilisateurs/import', () => import('@/modules/access/pages/ACC-W02.ImportUsers'));
const userDetailRoute = searchPage('/admin/utilisateurs/$userId', () => import('@/modules/access/pages/ACC-P09.UserDetail'), parseUserDetailSearch);
const rolesRoute = page('/admin/roles', () => import('@/modules/access/pages/ROL-P01.Roles'));
const rolePermissionsRoute = page('/admin/roles/$roleId', () => import('@/modules/access/pages/ROL-P02.RolePermissions'));
const viewAsRoute = searchPage('/admin/voir-en-tant-que', () => import('@/modules/access/pages/ROL-P03.ViewAs'), parseViewAsSearch);
const auditRoute = searchPage('/admin/audit', () => import('@/modules/access/pages/AUD-P01.AuditLog'), parseListSearch);
const auditStatsRoute = searchPage('/admin/audit/statistiques', () => import('@/modules/access/pages/AUD-P02.ActivityStats'), parsePeriodSearch);

// Référentiels (REF-P01 à REF-P07, REF-W01 — lot F2) : lecture pour tous les profils internes, gestion Administrateur.
const referentialsRoute = page('/referentiels', () => import('@/modules/referentials/pages/REF-P01.Referentials'));
const localitiesRoute = searchPage('/referentiels/localites', () => import('@/modules/referentials/pages/REF-P02.Localities'), parseLocalitySearch);
const localityImportRoute = page('/referentiels/localites/import', () => import('@/modules/referentials/pages/REF-W01.ImportLocalities'));
const partnersRoute = searchPage('/referentiels/partenaires', () => import('@/modules/referentials/pages/REF-P03.Partners'), parseListSearch);
const partnerDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/referentiels/partenaires/$partnerId',
  validateSearch: parseReferentialDetailSearch,
  component: lazyRouteComponent(() => import('@/modules/referentials/pages/REF-P05.PartnerDonorDetail'), 'PartnerDetail'),
});
const donorsRoute = searchPage('/referentiels/bailleurs', () => import('@/modules/referentials/pages/REF-P04.Donors'), parseListSearch);
const donorDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/referentiels/bailleurs/$donorId',
  validateSearch: parseReferentialDetailSearch,
  component: lazyRouteComponent(() => import('@/modules/referentials/pages/REF-P05.PartnerDonorDetail'), 'DonorDetail'),
});
const departmentsRoute = page('/referentiels/departements', () => import('@/modules/referentials/pages/REF-P06.Departments'));
// Formations sanitaires (lot D1d, ADR 0091).
const healthFacilitiesRoute = searchPage('/referentiels/formations-sanitaires', () => import('@/modules/referentials/pages/REF-P08.HealthFacilities'), parseListSearch);
const healthFacilitiesImportRoute = page('/referentiels/formations-sanitaires/import', () => import('@/modules/referentials/pages/REF-W02.ImportHealthFacilities'));
const listsRoute = page('/referentiels/listes', () => import('@/modules/referentials/pages/REF-P07.Lists'));
const listRoute = page('/referentiels/listes/$code', () => import('@/modules/referentials/pages/REF-P07.Lists'));
// Projets (PRJ-P01 à PRJ-P08, PRJ-W01, PRJ-W02 — lot F3). URL de la fiche : …/projets/AMR-2025-01/2 (code, vue).
const projectsRoute = searchPage('/projets', () => import('@/modules/projects/pages/PRJ-P01.Projects'), parseListSearch);
const myProjectsRoute = searchPage('/mes-projets', () => import('@/modules/projects/pages/PRJ-P02.MyProjects'), parseListSearch);
const projectCreateRoute = searchPage('/projets/nouveau', () => import('@/modules/projects/pages/PRJ-W01.CreateProject'), searchAs<WizardSearch>());
const projectImportRoute = page('/projets/import', () => import('@/modules/projects/pages/PRJ-W02.ImportProjects'));
const projectRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/projets/$code',
  beforeLoad: ({ params }) => {
    throw redirect({ to: '/projets/$code/$view', params: { code: params.code, view: '1' } });
  },
});
const projectEditRoute = page('/projets/$code/modifier', () => import('@/modules/projects/pages/PRJ-P04.EditProject'));
const projectCloseRoute = page('/projets/$code/cloture', () => import('@/modules/projects/pages/PRJ-P08.CloseProject'));
const indicatorRoute = page('/projets/$code/indicateurs/$indicatorId', () => import('@/modules/projects/pages/PRJ-P07.IndicatorDetail'));
const projectViewRoute = searchPage('/projets/$code/$view', () => import('@/modules/projects/pages/PRJ-P03.ProjectDetail'), searchAs<ProjectViewSearch & BudgetViewSearch & { apercu?: string }>());

// Import du cadre logique et des activités (PRJ-W03, PRJ-26 — lot F4).
const logframeImportRoute = page('/projets/$code/import-cadre-logique', () => import('@/modules/activities/pages/PRJ-W03.ImportLogframe'));

// Activités, planning, jalons et livrables (ACT-P01 à P06, JAL-P01 à P03 — lot F4).
const activitiesRoute = searchPage('/activites', () => import('@/modules/activities/pages/ACT-P01.Activities'), searchAs<ActivitiesSearch>());
const myActivitiesRoute = searchPage('/mes-activites', () => import('@/modules/activities/pages/ACT-P02.MyActivities'), parseListSearch);
const activityCreateRoute = searchPage('/activites/nouvelle', () => import('@/modules/activities/pages/ACT-P05.ActivityForm'), searchAs<ActivityFormSearch>());
const activityDetailRoute = searchPage('/activites/$activityId', () => import('@/modules/activities/pages/ACT-P03.ActivityDetail'), searchAs<ActivityDetailSearch>());
const activityEditRoute = page('/activites/$activityId/modifier', () => import('@/modules/activities/pages/ACT-P05.ActivityForm'));
const planningRoute = searchPage('/planning', () => import('@/modules/activities/pages/ACT-P06.ActivityCalendar'), searchAs<CalendarSearch>());
// Gantt multi-projets (ACT-P07, ACT-09, lot F13b).
const ganttRoute = searchPage('/planning/gantt', () => import('@/modules/activities/pages/ACT-P07.Gantt'), searchAs<GanttSearch>());
const milestonesRoute = searchPage('/jalons', () => import('@/modules/activities/pages/JAL-P01.Milestones'), parseListSearch);
const milestoneRoute = page('/jalons/$milestoneId', () => import('@/modules/activities/pages/JAL-P02.MilestoneDetail'));
const reviewRoute = searchPage('/livrables-a-valider', () => import('@/modules/activities/pages/JAL-P03.DeliverablesReview'), searchAs<ReviewSearch>());

// Collecte terrain (COL-P08 à COL-P15, COL-W01 web, COL-W02 — lot F5).
const collectionsRoute = searchPage('/collectes', () => import('@/modules/field/pages/COL-P10.Collections'), searchAs<CollectionsSearch>());
const collectionsQueueRoute = searchPage('/collectes/a-valider', () => import('@/modules/field/pages/COL-P08.ValidationQueue'), searchAs<QueueSearch>());
const collectionDetailRoute = searchPage('/collectes/$collectionId', () => import('@/modules/field/pages/COL-P09.CollectionDetail'), searchAs<CollectionDetailSearch>());
const collectionEditRoute = page('/collectes/$collectionId/modifier', () => import('@/modules/field/pages/COL-W01.CollectionEntry'));
const collectionCreateRoute = page('/activites/$activityId/collecte', () => import('@/modules/field/pages/COL-W01.CollectionEntry'));
const activityParticipantsRoute = searchPage('/activites/$activityId/participants', () => import('@/modules/field/pages/COL-P12.Participants'), searchAs<ParticipantsSearch>());
const activityParticipantsImportRoute = page('/activites/$activityId/participants/import', () => import('@/modules/field/pages/COL-W02.ImportParticipants'));
const projectParticipantsRoute = searchPage('/projets/$code/participants', () => import('@/modules/field/pages/COL-P12.Participants'), searchAs<ParticipantsSearch>());
const completenessRoute = page('/projets/$code/completude', () => import('@/modules/field/pages/COL-P11.Completeness'));
const formsRoute = searchPage('/admin/formulaires', () => import('@/modules/field/pages/COL-P13.Forms'), parseListSearch);
const formCreateRoute = page('/admin/formulaires/nouveau', () => import('@/modules/field/pages/COL-P14.FormBuilder'));
const formBuilderRoute = searchPage('/admin/formulaires/$formId', () => import('@/modules/field/pages/COL-P14.FormBuilder'), searchAs<BuilderSearch>());

// Budget et dépenses projet (BUD-P01 à BUD-P07, BUD-P09 — lot F6). L'onglet Budget de la fiche est PRJ-P03.V7.
const budgetsRoute = searchPage('/budgets', () => import('@/modules/budget/pages/BUD-P01.Budgets'), searchAs<BudgetsSearch>());
const expensesRoute = searchPage('/depenses', () => import('@/modules/budget/pages/BUD-P03.Expenses'), searchAs<ExpensesSearch>());
const expensesQueueRoute = searchPage('/depenses/a-valider', () => import('@/modules/budget/pages/BUD-P06.ValidationQueue'), searchAs<ExpenseQueueSearch>());
const expenseCreateRoute = searchPage('/depenses/nouvelle', () => import('@/modules/budget/pages/BUD-P04.ExpenseEntry'), searchAs<ExpenseEntrySearch>());
const expenseDetailRoute = page('/depenses/$expenseId', () => import('@/modules/budget/pages/BUD-P05.ExpenseDetail'));
const expenseEditRoute = page('/depenses/$expenseId/modifier', () => import('@/modules/budget/pages/BUD-P04.ExpenseEntry'));
const budgetPlanRoute = page('/projets/$code/budget', () => import('@/modules/budget/pages/BUD-P02.BudgetPlan'));
const budgetComparisonRoute = searchPage('/projets/$code/budget/comparatif', () => import('@/modules/budget/pages/BUD-P09.Comparison'), searchAs<ComparisonSearch>());
const proposalsRoute = searchPage('/projets/$code/propositions', () => import('@/modules/budget/pages/BUD-P07.Proposals'), searchAs<ProposalsSearch>());
// Lot F11b-1 (BUD-06) : engagements du projet par ligne budgétaire.
const commitmentsRoute = page('/projets/$code/engagements', () => import('@/modules/budget/pages/BUD-P08.Commitments'));

// Rapports (RAP-P01 à RAP-P05 — lot F7b) ; l'onglet Rapports de la fiche est PRJ-P03.V9. Analytique (ANA-P01, ANA-P02).
const reportsRoute = searchPage('/rapports', () => import('@/modules/reports/pages/RAP-P01.Reports'), searchAs<ReportsSearch>());
const myReportsRoute = searchPage('/mes-rapports', () => import('@/modules/reports/pages/RAP-P02.MyReports'), searchAs<ReportsSearch>());
const reportCreateRoute = searchPage('/rapports/nouveau', () => import('@/modules/reports/pages/RAP-P03.ReportEditor'), searchAs<ReportEditorSearch>());
const generateReportRoute = searchPage('/rapports/generer', () => import('@/modules/reports/pages/RAP-W01.GenerateReport'), searchAs<GenerateReportSearch>());
const reportTemplatesRoute = page('/rapports/modeles', () => import('@/modules/reports/pages/RAP-P06.ReportTemplates'));
const reportsQueueRoute = searchPage('/rapports/a-valider', () => import('@/modules/reports/pages/RAP-P05.ValidationQueue'), searchAs<ReportQueueSearch>());
const reportDetailRoute = page('/rapports/$reportId', () => import('@/modules/reports/pages/RAP-P04.ReportDetail'));
const reportEditRoute = page('/rapports/$reportId/modifier', () => import('@/modules/reports/pages/RAP-P03.ReportEditor'));
const explorerRoute = searchPage('/analyses', () => import('@/modules/analytics/pages/ANA-P01.Explorer'), searchAs<ExplorerSearch>());
const customReportsRoute = searchPage('/analyses/rapports', () => import('@/modules/analytics/pages/ANA-P03.CustomReports'), searchAs<CustomReportsSearch>());
const rawExportRoute = searchPage('/analyses/export', () => import('@/modules/analytics/pages/ANA-P02.RawExport'), searchAs<RawExportSearch>());
// Lot F18c : analyses avancées, emplacement réservé (ANA-P04, ANA-06).
const advancedAnalyticsRoute = page('/analyses/avancees', () => import('@/modules/analytics/pages/ANA-P04.AdvancedAnalytics'));

// Messagerie et annonces (COM-P01 à COM-P03 — lot F8) ; l'onglet Communication de la fiche est PRJ-P03.V12.
const messagingRoute = searchPage('/messagerie', () => import('@/modules/comms/pages/COM-P01.Messaging'), searchAs<{ fil?: string; message?: string }>());
const conversationRoute = searchPage('/messagerie/$channelId', () => import('@/modules/comms/pages/COM-P01.Messaging'), searchAs<{ fil?: string; message?: string }>());
const channelSettingsRoute = searchPage('/messagerie/$channelId/parametres', () => import('@/modules/comms/pages/COM-P02.ChannelSettings'), searchAs<{ onglet?: 'membres' | 'parametres' | 'fichiers' }>());
const announcementsRoute = searchPage('/annonces', () => import('@/modules/comms/pages/COM-P03.Announcements'), searchAs<{ annonce?: string; page?: number }>());
// Lot F16b : espace d'équipe d'un canal (tableau de bord et kanban COM-P07, réunions COM-P06, calendrier COM-P05).
const teamPages = () => import('@/modules/comms/pages/team-pages');
const team = (name: keyof Awaited<ReturnType<typeof teamPages>>) => () => teamPages().then((m) => ({ default: m[name] }));
const teamDashboardRoute = searchPage('/equipe', team('TeamDashboard'), searchAs<{ equipe?: string }>());
const teamMeetingsRoute = searchPage('/equipe/reunions', team('TeamMeetings'), searchAs<{ equipe?: string }>());
const teamMeetingRoute = page('/equipe/reunions/$meetingId', team('TeamMeeting'));
const teamSpaceCalendarRoute = searchPage('/equipe/calendrier', team('TeamCalendar'), searchAs<{ equipe?: string; vue?: 'semaine' | 'mois' | 'liste'; date?: string; couches?: string }>());
// Lot F16c : documents partagés et co-édition (COM-P04), module à part (l'éditeur co-édité se charge à l'ouverture d'un texte).
const documentPages = () => import('@/modules/comms/pages/document-pages');
const teamDocument = (name: keyof Awaited<ReturnType<typeof documentPages>>) => () => documentPages().then((m) => ({ default: m[name] }));
const teamDocumentsRoute = searchPage('/equipe/documents', teamDocument('TeamDocuments'), searchAs<{ equipe?: string; dossier?: string; q?: string }>());
const teamDocumentRoute = page('/equipe/documents/$documentId', teamDocument('TeamDocument'));
// Lot F16d : visioconférence (COM-P08 salle plein écran, COM-P09 salles permanentes, COM-P10 enregistrements).
const videoPages = () => import('@/modules/comms/pages/video-pages');
const video = (name: keyof Awaited<ReturnType<typeof videoPages>>) => () => videoPages().then((m) => ({ default: m[name] }));
const videoRoomsRoute = page('/visio/salles', video('VideoRooms'));
const videoRecordingsRoute = page('/visio/enregistrements', video('VideoRecordings'));
const videoRoomRoute = createRoute({ getParentRoute: () => focusRoute, path: '/visio/$roomId', component: lazyRouteComponent(video('VideoRoom')) });
// Lot D2 (ADR 0092) : invité sans compte, page publique (hors garde d'authentification).
const videoGuestRoute = publicPage('/visio/invite/$token', video('GuestJoin'));

// Ressources humaines (RH-P01 à RH-P10, RH-W01 — lot F10a).
const staffRoute = searchPage('/rh/personnel', () => import('@/modules/hr/pages/RH-P01.Staff'), parseListSearch);
const employeeCreateRoute = searchPage('/rh/personnel/nouveau', () => import('@/modules/hr/pages/RH-W01.NewEmployee'), searchAs<{ candidature?: string }>());
const employeeRoute = searchPage('/rh/personnel/$employeeId', () => import('@/modules/hr/pages/RH-P02.EmployeeDetail'), searchAs<{ onglet?: string }>());
const myFileRoute = searchPage('/rh/mon-dossier', () => import('@/modules/hr/pages/RH-P02.MyFile'), searchAs<{ onglet?: string }>());
const expiringDocumentsRoute = searchPage('/rh/pieces-a-echeance', () => import('@/modules/hr/pages/RH-P03.ExpiringDocuments'), parseListSearch);
const movementsRoute = searchPage('/rh/mouvements', () => import('@/modules/hr/pages/RH-P04.Movements'), parseListSearch);
const orgChartRoute = searchPage('/rh/organigramme', () => import('@/modules/hr/pages/RH-P05.OrgChart'), searchAs<{ vue?: 'liste'; lieu?: string; q?: string }>());
const directoryRoute = searchPage('/rh/annuaire', () => import('@/modules/hr/pages/RH-P06.Directory'), parseListSearch);
const myLeavesRoute = searchPage('/rh/mes-conges', () => import('@/modules/hr/pages/RH-P07.MyLeaves'), searchAs<{ demande?: '1' }>());
const leaveQueueRoute = searchPage('/rh/conges-a-traiter', () => import('@/modules/hr/pages/RH-P08.LeaveQueue'), searchAs<{ demande?: string }>());
const teamCalendarRoute = searchPage('/rh/calendrier', () => import('@/modules/hr/pages/RH-P09.TeamCalendar'), searchAs<{ mois?: string; departement?: string }>());
const dutyPlanningRoute = searchPage('/rh/presences', () => import('@/modules/hr/pages/RH-P10.DutyPlanning'), searchAs<{ semaine?: string; departement?: string }>());
const leaveSettingsRoute = searchPage('/rh/parametres-conges', () => import('@/modules/hr/pages/RH-S01.LeaveSettings'), searchAs<{ annee?: string }>());
// Paie (RH-W02, RH-P11 à RH-P15, RH-P02.V5/V8 — lot F10b).
const payrollRoute = searchPage('/rh/paie', () => import('@/modules/hr/pages/RH-W02.Payroll'), searchAs<{ mois?: string; etape?: number }>());
const payrollHistoryRoute = searchPage('/rh/paie/historique', () => import('@/modules/hr/pages/RH-P13.PayrollHistory'), searchAs<{ annee?: number; vue?: 'agent'; agent?: string }>());
const salaryGridRoute = page('/rh/paie/grille', () => import('@/modules/hr/pages/RH-P11.SalaryGrid'));
const paySettingsRoute = page('/rh/paie/parametres', () => import('@/modules/hr/pages/RH-P12.PaySettings'));
const allocationKeysRoute = searchPage('/rh/paie/repartition', () => import('@/modules/hr/pages/RH-P15.AllocationKeys'), searchAs<{ agent?: string }>());
const myPayslipsRoute = page('/rh/mes-bulletins', () => import('@/modules/hr/pages/RH-P14.MyPayslips'));
// Évaluations, recrutement, tableau de bord RH (lot F10c, ADR 0050).
const campaignsRoute = searchPage('/rh/evaluations', () => import('@/modules/hr/pages/RH-P17.Campaigns'), searchAs<{ campagne?: string }>());
const evaluationGridsRoute = searchPage('/rh/evaluations/grilles', () => import('@/modules/hr/pages/RH-P16.EvaluationGrids'), searchAs<{ grille?: string }>());
const evaluationRoute = page('/rh/evaluations/$evaluationId', () => import('@/modules/hr/pages/RH-P18.Evaluation'));
const myEvaluationsRoute = page('/rh/mes-evaluations', () => import('@/modules/hr/pages/RH-P18.MyEvaluations'));
const jobOffersRoute = page('/rh/recrutement', () => import('@/modules/hr/pages/RH-P20.JobOffers'));
const jobOfferCreateRoute = page('/rh/recrutement/nouvelle', () => import('@/modules/hr/pages/RH-P21.JobOfferForm'));
const candidatesRoute = page('/rh/recrutement/candidats', () => import('@/modules/hr/pages/RH-P22.Candidates'));
const pipelineRoute = searchPage('/rh/recrutement/$offerId', () => import('@/modules/hr/pages/RH-P22.Pipeline'), searchAs<{ vue?: 'tableau'; tri?: 'note' }>());
const jobOfferEditRoute = page('/rh/recrutement/$offerId/modifier', () => import('@/modules/hr/pages/RH-P21.JobOfferForm'));
const applicationRoute = page('/rh/candidatures/$applicationId', () => import('@/modules/hr/pages/RH-P23.Application'));
const recruitmentApprovalsRoute = searchPage('/rh/recrutements-a-approuver', () => import('@/modules/hr/pages/RH-P24.RecruitmentApprovals'), searchAs<{ candidature?: string }>());
const hrDashboardRoute = page('/rh/tableau-de-bord', () => import('@/modules/hr/pages/RH-P25.HrDashboard'));
// Comptabilité générale (FIN-P01 à P09, P29 à P31, D01, D03, M01 — lot F11a, ADR 0053).
type LedgerSearch = { du?: string; au?: string; compte?: string; projet?: string };
const entriesRoute = searchPage('/finance/ecritures', () => import('@/modules/finance/pages/FIN-P04.Entries'), parseListSearch);
const entryCreateRoute = searchPage('/finance/ecritures/nouvelle', () => import('@/modules/finance/pages/FIN-P05.EntryForm'), searchAs<{ date?: string }>());
const entryDetailRoute = page('/finance/ecritures/$entryId', () => import('@/modules/finance/pages/FIN-P06.EntryDetail'));
const entryEditRoute = page('/finance/ecritures/$entryId/modifier', () => import('@/modules/finance/pages/FIN-P05.EntryForm'));
const entriesQueueRoute = searchPage('/finance/a-valider', () => import('@/modules/finance/pages/FIN-P07.ValidationQueue'), searchAs<{ ecriture?: string }>());
const accountsRoute = searchPage('/finance/plan-comptable', () => import('@/modules/finance/pages/FIN-P01.ChartOfAccounts'), searchAs<{ classe?: string; q?: string; afficher?: 'active' | 'inactive' | 'all' }>());
const accountsImportRoute = page('/finance/plan-comptable/import', () => import('@/modules/finance/pages/FIN-P01.ImportAccounts'));
const fiscalYearsRoute = page('/finance/exercices', () => import('@/modules/finance/pages/FIN-P02.FiscalYears'));
const journalsRoute = page('/finance/journaux', () => import('@/modules/finance/pages/FIN-P03.Journals'));
const exchangeRatesRoute = page('/finance/taux-de-change', () => import('@/modules/finance/pages/FIN-P29.ExchangeRates'));
const accountingExportRoute = page('/finance/export', () => import('@/modules/finance/pages/FIN-P31.AccountingExport'));
const ledgerRoute = searchPage('/finance/grand-livre', () => import('@/modules/finance/pages/FIN-P08.GeneralLedger'), searchAs<LedgerSearch>());
const trialBalanceRoute = searchPage('/finance/balance', () => import('@/modules/finance/pages/FIN-P09.TrialBalance'), searchAs<LedgerSearch>());
const supportingDocumentsRoute = searchPage('/finance/pieces', () => import('@/modules/finance/pages/FIN-P30.SupportingDocuments'), searchAs<{ tout?: '1'; projet?: string; type?: 'entry' | 'expense'; mois?: string }>());
// Achats et paiements (lot F11b-1, ADR 0054) : FIN-P17 à FIN-P25.
const purchaseOrdersRoute = searchPage('/finance/bons-de-commande', () => import('@/modules/finance/pages/FIN-P19.PurchaseOrders'), parseListSearch);
const purchaseOrderCreateRoute = searchPage('/finance/bons-de-commande/nouveau', () => import('@/modules/finance/pages/FIN-P20.PurchaseOrderForm'), searchAs<{ fournisseur?: string; projet?: string }>());
const purchaseOrderDetailRoute = page('/finance/bons-de-commande/$orderId', () => import('@/modules/finance/pages/FIN-P20.PurchaseOrderDetail'));
const purchaseOrderEditRoute = page('/finance/bons-de-commande/$orderId/modifier', () => import('@/modules/finance/pages/FIN-P20.PurchaseOrderForm'));
const invoicesRoute = searchPage('/finance/factures', () => import('@/modules/finance/pages/FIN-P21.Invoices'), parseListSearch);
const invoiceCreateRoute = searchPage('/finance/factures/nouvelle', () => import('@/modules/finance/pages/FIN-P22.InvoiceForm'), searchAs<{ commande?: string; fournisseur?: string }>());
const invoiceDetailRoute = page('/finance/factures/$invoiceId', () => import('@/modules/finance/pages/FIN-P22.InvoiceDetail'));
const invoiceEditRoute = page('/finance/factures/$invoiceId/modifier', () => import('@/modules/finance/pages/FIN-P22.InvoiceForm'));
const suppliersRoute = searchPage('/finance/fournisseurs', () => import('@/modules/finance/pages/FIN-P17.Suppliers'), parseListSearch);
const supplierDetailRoute = searchPage('/finance/fournisseurs/$supplierId', () => import('@/modules/finance/pages/FIN-P18.SupplierDetail'), searchAs<{ onglet?: 'info' | 'orders' | 'invoices' | 'payments' }>());
const circuitsRoute = page('/finance/circuits', () => import('@/modules/finance/pages/FIN-P23.ApprovalCircuits'));
const paymentsRoute = searchPage('/finance/paiements', () => import('@/modules/finance/pages/FIN-P24.Payments'), parseListSearch);
const paymentCreateRoute = searchPage('/finance/paiements/nouveau', () => import('@/modules/finance/pages/FIN-P25.PaymentForm'), searchAs<{ facture?: string; depense?: string }>());
// Lot F11b-2 (ADR 0055) : caisse (FIN-P26), lettrage (FIN-P10), rapprochement bancaire (FIN-P11).
const cashDeskRoute = searchPage('/finance/caisse', () => import('@/modules/finance/pages/FIN-P26.CashDesk'), parseListSearch);
const letteringRoute = searchPage('/finance/lettrage', () => import('@/modules/finance/pages/FIN-P10.Lettering'), searchAs<{ compte?: string; tiers?: string; etat?: 'unlettered' | 'lettered' | 'all'; page?: number }>());
const reconciliationsRoute = page('/finance/rapprochement', () => import('@/modules/finance/pages/FIN-P11.Reconciliations'));
// Lot F11c-1 (ADR 0056) : budget institutionnel (FIN-P13), consolidation (FIN-P14), virements (FIN-P15), prévisions (FIN-P16).
const institutionalBudgetRoute = searchPage('/finance/budget-institutionnel', () => import('@/modules/finance/pages/FIN-P13.InstitutionalBudget'), searchAs<{ exercice?: number }>());
const consolidationRoute = searchPage('/finance/budgets-consolides', () => import('@/modules/finance/pages/FIN-P14.BudgetConsolidation'), searchAs<{ vue?: 'donor' | 'project'; exercice?: number; groupe?: string }>());
const transfersRoute = searchPage('/finance/virements', () => import('@/modules/finance/pages/FIN-P15.BudgetTransfers'), searchAs<{ onglet?: 'mine' | 'to_approve' | 'all'; virement?: string }>());
const forecastsRoute = page('/finance/previsions', () => import('@/modules/finance/pages/FIN-P16.CashForecasts'));
// Lot F11c-2 (ADR 0057) : clôture d'exercice (FIN-W01), états financiers (FIN-P12).
const closingRoute = searchPage('/finance/exercices/$code/cloture', () => import('@/modules/finance/pages/FIN-W01.Closing'), searchAs<{ etape?: number }>());
const statementsRoute = searchPage('/finance/etats-financiers', () => import('@/modules/finance/pages/FIN-P12.FinancialStatements'), searchAs<{ exercice?: string; etat?: 'bilan' | 'resultat' | 'flux' | 'notes' }>());
// Lot F11c-3 (ADR 0058) : tableau de bord financier (FIN-P27), modèles bailleurs (FIN-P28), rapport bailleur (FIN-W02).
const financialDashboardRoute = page('/finance/tableau-de-bord', () => import('@/modules/finance/pages/FIN-P27.FinancialDashboard'));
const donorTemplatesRoute = searchPage('/finance/modeles-bailleurs', () => import('@/modules/finance/pages/FIN-P28.DonorTemplates'), searchAs<{ modele?: string; projet?: string }>());
const donorReportRoute = searchPage('/finance/rapport-bailleur', () => import('@/modules/finance/pages/FIN-W02.DonorReport'), searchAs<{ modele?: string; projet?: string; du?: string; au?: string; devise?: string; etape?: number }>());
const reconciliationRoute = searchPage('/finance/rapprochement/$reconciliationId', () => import('@/modules/finance/pages/FIN-P11.ReconciliationWizard'), searchAs<{ etape?: number }>());

const settingsRoute = page('/admin/parametres', () => import('@/modules/platform/pages/TRV-P01.OrganizationSettings'));
const ssoRoute = page('/admin/parametres/sso', () => import('@/modules/platform/pages/ACC-P10.SingleSignOn'));
const templatesRoute = searchPage('/admin/modeles', () => import('@/modules/platform/pages/TRV-P02.Templates'), parseListSearch);
// Lot F17 — intégrations (INT-P01, INT-P04 à INT-P06).
const integrationsRoute = page('/admin/integrations', () => import('@/modules/integrations/pages/INT-P01.Integrations'));
const emailIntegrationRoute = page('/admin/integrations/email', () => import('@/modules/integrations/pages/INT-P05.EmailIntegration'));
const mobileMoneyIntegrationRoute = page('/admin/integrations/mobile-money', () => import('@/modules/integrations/pages/INT-P04.MobileMoneyIntegration'));
const smsIntegrationRoute = page('/admin/integrations/sms', () => import('@/modules/integrations/pages/INT-P06.SmsIntegration'));
// Lot F17e (INT-06, ADR 0085) : signature électronique — INT-P07, signer un document (INT-S01), mes signatures (INT-S02).
const esignatureIntegrationRoute = page('/admin/integrations/esignature', () => import('@/modules/integrations/pages/INT-P07.ESignatureIntegration'));
// Lot F17f (INT-02, ADR 0086) : DHIS2 — connexion, projets transmis, correspondances, aperçu et envoi, historique.
const dhis2IntegrationRoute = page('/admin/integrations/dhis2', () => import('@/modules/integrations/pages/INT-P03.Dhis2Integration'));
const koboIntegrationRoute = page('/admin/integrations/kobo', () => import('@/modules/integrations/pages/INT-P02.KoboIntegration'));
const signDocumentRoute = page('/signatures/$requestId', () => import('@/modules/integrations/pages/INT-S01.SignDocument'));
const mySignaturesRoute = page('/moi/signatures', () => import('@/modules/integrations/pages/INT-S02.MySignatures'));
const trashRoute = searchPage('/admin/corbeille', () => import('@/modules/platform/pages/TRV-P03.Trash'), parseListSearch);
const fullExportRoute = page('/admin/export-complet', () => import('@/modules/platform/pages/TRV-P04.FullExport'));
const importsRoute = searchPage('/imports', () => import('@/modules/platform/pages/TRV-P06.ImportHistory'), parseListSearch);

// Vie associative (ASSO-P01 à P05, ASSO-W01 ; ASSO-01 à ASSO-06, lot F12a).
const managementPages = () => import('@/modules/association/pages/management-pages');
const asso = (name: keyof Awaited<ReturnType<typeof managementPages>>) => () => managementPages().then((m) => ({ default: m[name] }));
const membershipRequestsRoute = searchPage('/association/demandes', asso('MembershipRequests'), searchAs<{ demande?: string; statut?: string }>());
const membersRoute = searchPage('/association/membres', asso('Members'), parseListSearch);
const memberCreateRoute = page('/association/membres/nouveau', asso('MemberForm'));
const memberImportRoute = page('/association/membres/import', () => import('@/modules/association/pages/ASSO-W01.ImportMembers'));
const memberRoute = searchPage('/association/membres/$memberId', asso('Member'), searchAs<{ onglet?: string }>());
const memberEditRoute = page('/association/membres/$memberId/modifier', asso('MemberForm'));
const memberDirectoryRoute = searchPage('/association/annuaire', () => import('@/modules/association/pages/ASSO-P04.Directory'), parseListSearch);
const myMemberCardRoute = page('/mon-adhesion/carte', () => import('@/modules/association/pages/ASSO-P05.MyCard'));
// Cotisations (ASSO-P06, ASSO-P07, ASSO-P09 ; ASSO-07, ASSO-08, ASSO-11, ASSO-12, lot F12b).
const yearSearch = searchAs<{ annee?: number }>();
const feesRoute = searchPage('/association/cotisations', asso('FeeTracking'), parseListSearch);
const feeScheduleRoute = searchPage('/association/cotisations/bareme', asso('FeeSchedule'), yearSearch);
const renewalsRoute = searchPage('/association/cotisations/renouvellements', asso('Renewals'), yearSearch);
// Gouvernance (ASSO-P10 à ASSO-P17, ASSO-W02 ; ASSO-13 à ASSO-20, lot F12c).
const governancePages = () => import('@/modules/association/pages/governance-pages');
const gov = (name: keyof Awaited<ReturnType<typeof governancePages>>) => () => governancePages().then((m) => ({ default: m[name] }));
const meetingsRoute = searchPage('/association/reunions', gov('Meetings'), parseListSearch);
const planMeetingRoute = searchPage('/association/reunions/planifier', gov('PlanMeeting'), searchAs<{ reunion?: string }>());
const meetingRoute = searchPage('/association/reunions/$meetingId', gov('MeetingDetail'), searchAs<{ onglet?: string }>());
const attendanceRoute = page('/association/reunions/$meetingId/emargement', gov('Attendance'));
const minutesRoute = page('/association/reunions/$meetingId/proces-verbal', gov('Minutes'));
const instancesRoute = page('/association/instances', gov('Instances'));
const mandatesRoute = searchPage('/association/mandats', gov('Mandates'), searchAs<{ instance?: string }>());
const deliberationsRoute = searchPage('/association/registre', gov('Register'), parseListSearch);
const voteRoute = page('/association/votes/$resolutionId', gov('Vote'));
// Communication et pilotage (ASSO-P18 à ASSO-P21 ; ASSO-21 à ASSO-25, DSH-15, lot F12d).
const communicationPages = () => import('@/modules/association/pages/communication-pages');
const com = (name: keyof Awaited<ReturnType<typeof communicationPages>>) => () => communicationPages().then((m) => ({ default: m[name] }));
const associationDashboardRoute = page('/association/tableau-de-bord', com('Dashboard'));
const newslettersRoute = searchPage('/association/newsletters', com('Newsletters'), parseListSearch);
const newNewsletterRoute = page('/association/newsletters/nouvelle', com('NewsletterEditor'));
const newsletterRoute = page('/association/newsletters/$newsletterId', com('NewsletterEditor'));
const memberAnnouncementsRoute = page('/association/annonces', com('Announcements'));
const memberDocumentsRoute = searchPage('/association/documents', com('Documents'), parseListSearch);

// Académie (lot F14a, ADR 0069 ; ACA-01 à ACA-10) : catalogue public sans compte (PUB-P05, PUB-P06, décision AMR
// 16.1) ; catalogue interne (ACA-P01), gestion des formations (ACA-P02, ACA-W01, ACA-P03), modules et quiz (ACA-P06,
// ACA-P07), sessions (ACA-P05) et formateurs (ACA-P04).
const publicCatalogRoute = publicSearchPage('/formations', () => import('@/modules/academy/pages/PUB-P05.PublicCatalog'), searchAs<Record<string, unknown>>());
const publicCourseRoute = publicPage('/formations/$slug', () => import('@/modules/academy/pages/PUB-P06.PublicCourse'));
// Lot F14d (ADR 0072) : vérification publique d'une attestation (PUB-P11 ; QR de l'attestation ACA-G01).
const verifyCertificateRoute = publicPage('/verifier/attestation', () => import('@/modules/academy/pages/PUB-P11.VerifyCertificate'));
const verifyCertificateCodeRoute = publicPage('/verifier/attestation/$code', () => import('@/modules/academy/pages/PUB-P11.VerifyCertificate'));
// Lot F17e : vérification publique d'un document signé (PUB-S01 ; QR du certificat de signature).
const verifySignatureRoute = publicPage('/verifier/signature', () => import('@/modules/integrations/pages/PUB-S01.VerifySignature'));
const verifySignatureCodeRoute = publicPage('/verifier/signature/$code', () => import('@/modules/integrations/pages/PUB-S01.VerifySignature'));
const academyPages = () => import('@/modules/academy/pages/academy-pages');
const aca = (name: keyof Awaited<ReturnType<typeof academyPages>>) => () => academyPages().then((m) => ({ default: m[name] }));
const catalogRoute = searchPage('/academie', aca('Catalog'), searchAs<Record<string, unknown>>());
const catalogCourseRoute = page('/academie/formations/$slug', aca('CatalogCourse'));
// Lot F14b (ADR 0070) : inscription (ACA-W02), inscrits et liste d'attente (ACA-P08), « Mes formations » (ACA-P13).
const enrollRoute = searchPage('/academie/formations/$slug/inscription', aca('Enroll'), searchAs<{ session?: string }>());
const myCoursesRoute = searchPage('/academie/mes-formations', aca('MyCourses'), searchAs<{ onglet?: string }>());
const enrollmentsRoute = searchPage('/academie/gestion/$courseId/inscriptions', aca('Enrollments'), searchAs<{ session?: string; liste?: string; q?: string }>());
const coursesRoute = searchPage('/academie/gestion', aca('Courses'), parseListSearch);
const newCourseRoute = page('/academie/gestion/nouvelle', aca('CourseWizard'));
const courseRoute = searchPage('/academie/gestion/$courseId', aca('Course'), searchAs<{ onglet?: string }>());
const editCourseRoute = page('/academie/gestion/$courseId/modifier', aca('CourseWizard'));
const newModuleRoute = page('/academie/gestion/$courseId/modules/nouveau', aca('ModuleEditor'));
const moduleRoute = page('/academie/modules/$moduleId', aca('ModuleEditor'));
const quizRoute = page('/academie/modules/$moduleId/questions', aca('QuizEditor'));
const sessionsRoute = searchPage('/academie/sessions', aca('Sessions'), searchAs<{ mois?: string; vue?: string }>());
// Lot F14c (ADR 0071) : lecteur (ACA-P09) et questionnaire (ACA-P12) plein écran, corrections (ACA-P10), émargement (ACA-P11).
const learnRoute = focusPage('/academie/apprendre/$enrollmentId', aca('Learn'), searchAs<{ module?: string }>());
const satisfactionRoute = focusPage('/academie/apprendre/$enrollmentId/avis', aca('Satisfaction'), searchAs<Record<string, never>>());
const gradingRoute = searchPage('/academie/corrections', aca('Grading'), searchAs<{ onglet?: 'notes'; devoir?: string; formation?: string }>());
const academyAttendanceRoute = searchPage('/academie/emargement', aca('Attendance'), searchAs<{ session?: string; jour?: string }>());
// Lot F14d : tableau de bord de l'Académie (ACA-P14).
const academyDashboardRoute = searchPage('/academie/tableau-de-bord', aca('AcademyDashboard'), searchAs<{ annee?: number }>());
const trainersRoute = searchPage('/academie/formateurs', aca('Trainers'), (raw) => ({ ...parseListSearch(raw), ...(typeof raw.formateur === 'string' ? { formateur: raw.formateur } : {}) }));

// Capitalisation (lot F15a, ADR 0073 ; CAP-01 à CAP-10) : bibliothèque (CAP-P01), fiche et versions (CAP-P02),
// rédaction (CAP-P03 ; `?projet=` depuis PRJ-P08 « Rédiger la fiche »).
const knowledgePages = () => import('@/modules/knowledge/pages/knowledge-pages');
const cap = (name: keyof Awaited<ReturnType<typeof knowledgePages>>) => () => knowledgePages().then((m) => ({ default: m[name] }));
const knowledgeLibraryRoute = searchPage('/capitalisation', cap('Library'), searchAs<Record<string, unknown>>());
const knowledgeNewRoute = searchPage('/capitalisation/nouvelle', cap('KnowledgeEditor'), searchAs<{ projet?: string }>());
const knowledgeItemRoute = searchPage('/capitalisation/$itemId', cap('KnowledgeItem'), searchAs<{ onglet?: string }>());
const knowledgeEditRoute = page('/capitalisation/$itemId/modifier', cap('KnowledgeEditor'));
// Lot F15b (ADR 0074 ; CAP-11 à CAP-13) : publications à valider (CAP-P06, entrée HOME-P01), liens partagés (CAP-P07),
// rapports de capitalisation (CAP-P08) ; document partagé par lien, sans compte (PUB-P12).
const knowledgePublicationsRoute = searchPage('/capitalisation/publications', cap('Publications'), searchAs<{ fiche?: string; statut?: string }>());
const knowledgeSharesRoute = searchPage('/capitalisation/partages', cap('Shares'), searchAs<{ etat?: string; fiche?: string; page?: number }>());
const knowledgeReportsRoute = searchPage('/capitalisation/rapports', cap('Reports'), searchAs<{ page?: number; composer?: boolean }>());
// Lot F15c (ADR 0075 ; CAP-05) : archives terrain (CAP-P05).
const knowledgeArchivesRoute = searchPage('/capitalisation/archives', cap('FieldArchives'), searchAs<{ projet?: string; localite?: string; activite?: string; annee?: number; page?: number }>());
const sharedDocumentRoute = publicPage('/partage/$token', () => import('@/modules/knowledge/pages/PUB-P12.SharedDocument'));

// Planche des états transverses ETAT-01 à 10 : développement, démonstration et tests visuels uniquement.
const showcaseRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/demo/etats',
  beforeLoad: () => {
    if (!import.meta.env.DEV && import.meta.env.VITE_SHOWCASE !== 'true') throw redirect({ to: '/moi/profil' });
  },
  component: lazyRouteComponent(() => import('@/modules/states/pages/StatesShowcase')),
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  twoFactorRoute,
  forgotRoute,
  resetRoute,
  activationRoute,
  refusalRoute,
  signedOutRoute,
  registerRoute,
  verifyRoute,
  publicOffersRoute,
  publicOfferRoute,
  publicApplyRoute,
  portalRoute,
  membershipRequestRoute,
  membershipTrackingRoute,
  verifyMemberRoute,
  verifyMemberTokenRoute,
  publicCatalogRoute,
  publicCourseRoute,
  verifyCertificateRoute,
  verifyCertificateCodeRoute,
  verifySignatureRoute,
  videoGuestRoute,
  verifySignatureCodeRoute,
  sharedDocumentRoute,
  focusRoute.addChildren([learnRoute, satisfactionRoute, videoRoomRoute]),
  appRoute.addChildren([
    indexRoute,
    mySpaceRoute,
    profileRoute,
    personalDataRoute,
    securityRoute,
    activityRoute,
    preferencesRoute,
    myExportsRoute,
    mySignaturesRoute,
    signDocumentRoute,
    notificationsRoute,
    notificationPreferencesRoute,
    alertRulesRoute,
    searchRoute,
    portfolioRoute,
    interventionMapRoute,
    comparisonRoute,
    myStatisticsRoute,
    statisticsRoute,
    usersRoute,
    userCreateRoute,
    userImportRoute,
    userDetailRoute,
    rolesRoute,
    rolePermissionsRoute,
    viewAsRoute,
    auditRoute,
    auditStatsRoute,
    referentialsRoute,
    localitiesRoute,
    localityImportRoute,
    partnersRoute,
    partnerDetailRoute,
    donorsRoute,
    donorDetailRoute,
    departmentsRoute,
    healthFacilitiesRoute,
    healthFacilitiesImportRoute,
    listsRoute,
    listRoute,
    projectsRoute,
    myProjectsRoute,
    projectCreateRoute,
    projectImportRoute,
    projectRoute,
    projectEditRoute,
    projectCloseRoute,
    indicatorRoute,
    projectViewRoute,
    logframeImportRoute,
    activitiesRoute,
    myActivitiesRoute,
    activityCreateRoute,
    activityDetailRoute,
    activityEditRoute,
    planningRoute,
    ganttRoute,
    milestonesRoute,
    milestoneRoute,
    reviewRoute,
    settingsRoute,
    rectificationsRoute,
    ssoRoute,
    templatesRoute,
    integrationsRoute,
    emailIntegrationRoute,
    mobileMoneyIntegrationRoute,
    smsIntegrationRoute,
    esignatureIntegrationRoute,
    koboIntegrationRoute,
    dhis2IntegrationRoute,
    trashRoute,
    collectionsRoute,
    collectionsQueueRoute,
    collectionDetailRoute,
    collectionEditRoute,
    collectionCreateRoute,
    activityParticipantsRoute,
    activityParticipantsImportRoute,
    projectParticipantsRoute,
    completenessRoute,
    formsRoute,
    formCreateRoute,
    formBuilderRoute,
    budgetsRoute,
    expensesRoute,
    expensesQueueRoute,
    expenseCreateRoute,
    expenseDetailRoute,
    expenseEditRoute,
    budgetPlanRoute,
    budgetComparisonRoute,
    proposalsRoute,
    commitmentsRoute,
    reportsRoute,
    myReportsRoute,
    reportCreateRoute,
    reportsQueueRoute,
    generateReportRoute,
    reportTemplatesRoute,
    reportDetailRoute,
    reportEditRoute,
    explorerRoute,
    rawExportRoute,
    customReportsRoute,
    advancedAnalyticsRoute,
    messagingRoute,
    conversationRoute,
    channelSettingsRoute,
    announcementsRoute,
    teamDashboardRoute,
    teamMeetingsRoute,
    teamMeetingRoute,
    teamSpaceCalendarRoute,
    teamDocumentsRoute,
    teamDocumentRoute,
    videoRoomsRoute,
    videoRecordingsRoute,
    staffRoute,
    employeeCreateRoute,
    employeeRoute,
    myFileRoute,
    expiringDocumentsRoute,
    movementsRoute,
    orgChartRoute,
    directoryRoute,
    myLeavesRoute,
    leaveQueueRoute,
    teamCalendarRoute,
    dutyPlanningRoute,
    leaveSettingsRoute,
    payrollRoute,
    payrollHistoryRoute,
    salaryGridRoute,
    paySettingsRoute,
    allocationKeysRoute,
    myPayslipsRoute,
    campaignsRoute,
    evaluationGridsRoute,
    evaluationRoute,
    myEvaluationsRoute,
    jobOffersRoute,
    jobOfferCreateRoute,
    candidatesRoute,
    pipelineRoute,
    jobOfferEditRoute,
    applicationRoute,
    recruitmentApprovalsRoute,
    hrDashboardRoute,
    entriesRoute,
    entryCreateRoute,
    entryDetailRoute,
    entryEditRoute,
    entriesQueueRoute,
    accountsRoute,
    accountsImportRoute,
    fiscalYearsRoute,
    journalsRoute,
    exchangeRatesRoute,
    accountingExportRoute,
    ledgerRoute,
    trialBalanceRoute,
    supportingDocumentsRoute,
    purchaseOrdersRoute,
    purchaseOrderCreateRoute,
    purchaseOrderDetailRoute,
    purchaseOrderEditRoute,
    invoicesRoute,
    invoiceCreateRoute,
    invoiceDetailRoute,
    invoiceEditRoute,
    suppliersRoute,
    supplierDetailRoute,
    circuitsRoute,
    paymentsRoute,
    paymentCreateRoute,
    cashDeskRoute,
    letteringRoute,
    reconciliationsRoute,
    reconciliationRoute,
    institutionalBudgetRoute,
    consolidationRoute,
    transfersRoute,
    forecastsRoute,
    closingRoute,
    statementsRoute,
    financialDashboardRoute,
    donorTemplatesRoute,
    donorReportRoute,
    fullExportRoute,
    importsRoute,
    membershipRequestsRoute,
    membersRoute,
    memberCreateRoute,
    memberImportRoute,
    memberRoute,
    memberEditRoute,
    memberDirectoryRoute,
    myMemberCardRoute,
    feesRoute,
    feeScheduleRoute,
    renewalsRoute,
    meetingsRoute,
    planMeetingRoute,
    meetingRoute,
    attendanceRoute,
    minutesRoute,
    instancesRoute,
    mandatesRoute,
    deliberationsRoute,
    voteRoute,
    associationDashboardRoute,
    newslettersRoute,
    newNewsletterRoute,
    newsletterRoute,
    memberAnnouncementsRoute,
    memberDocumentsRoute,
    catalogRoute,
    catalogCourseRoute,
    enrollRoute,
    myCoursesRoute,
    enrollmentsRoute,
    coursesRoute,
    newCourseRoute,
    courseRoute,
    editCourseRoute,
    newModuleRoute,
    moduleRoute,
    quizRoute,
    sessionsRoute,
    trainersRoute,
    gradingRoute,
    academyAttendanceRoute,
    academyDashboardRoute,
    knowledgeLibraryRoute,
    knowledgeNewRoute,
    knowledgeItemRoute,
    knowledgeEditRoute,
    knowledgePublicationsRoute,
    knowledgeSharesRoute,
    knowledgeReportsRoute,
    knowledgeArchivesRoute,
    showcaseRoute,
  ]),
]);

export function createAppRouter(history?: RouterHistory) {
  return createRouter({
    routeTree,
    history,
    // Préchargement au survol, sauf si l'utilisateur économise ses données (TRV-09).
    defaultPreload: prefersDataSaving() ? false : 'intent',
    defaultPreloadDelay: 150,
    scrollRestoration: true,
  });
}

export const router = createAppRouter();

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
